import {
  UseQueryOptions,
  UseQueryResult,
  useQuery,
  useMutation,
  UseMutationOptions,
  UseMutationResult,
  useQueryClient,
} from '@tanstack/react-query';
import { IFHError } from '../../../../apis/HttpBaseApiWrapper';
import { useHealthAndSafetyService } from '../../../../apis/healthAndSafety/useHealthAndSafetyService';
import {
  TEmergencyContact,
  TEmergencyContactListApiResponse,
  TEmergencyContactParams,
} from '../../../../apis/healthAndSafety/healthAndSafety.api.interfaces';

type TQueryError = IFHError<{ message: string }>;
type TMutationError = IFHError<{ message: string }>;

// Query keys
export const getEmergencyContactListKey = 'get-emergency-contact-list';
export const getEmergencyContactByIdKey = 'get-emergency-contact-by-id';

// ===================== LIST HOOK =====================
export const useEmergencyContactListHook = (
  params: TEmergencyContactParams,
  options?: UseQueryOptions<TEmergencyContactListApiResponse, TQueryError>
): UseQueryResult<TEmergencyContactListApiResponse, TQueryError> => {
  const { getEmergencyContactList } = useHealthAndSafetyService();

  return useQuery<TEmergencyContactListApiResponse, TQueryError>({
    queryKey: [getEmergencyContactListKey, params],
    queryFn: () => getEmergencyContactList(params),
    enabled: true,
    ...options,
  });
};

// ===================== GET BY ID HOOK =====================
export const useEmergencyContactByIdHook = (
  id: string,
  options?: UseQueryOptions<TEmergencyContact, TQueryError>
): UseQueryResult<TEmergencyContact, TQueryError> => {
  const { getEmergencyContactById } = useHealthAndSafetyService();

  return useQuery<TEmergencyContact, TQueryError>({
    queryKey: [getEmergencyContactByIdKey, id],
    queryFn: () => getEmergencyContactById(id),
    enabled: Boolean(id),
    ...options,
  });
};

// ===================== CREATE HOOK =====================
export const useCreateEmergencyContactHook = (
  options?: UseMutationOptions<
    TEmergencyContact,
    TMutationError,
    Omit<TEmergencyContact, 'id' | 'createdAt' | 'updatedAt'>
  >
): UseMutationResult<
  TEmergencyContact,
  TMutationError,
  Omit<TEmergencyContact, 'id' | 'createdAt' | 'updatedAt'>
> => {
  const { createEmergencyContact } = useHealthAndSafetyService();
  const queryClient = useQueryClient();

  return useMutation<
    TEmergencyContact,
    TMutationError,
    Omit<TEmergencyContact, 'id' | 'createdAt' | 'updatedAt'>
  >({
    mutationFn: (contact) => createEmergencyContact(contact),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [getEmergencyContactListKey] });
    },
    ...options,
  });
};

// ===================== UPDATE HOOK =====================
export const useUpdateEmergencyContactHook = (
  options?: UseMutationOptions<
    TEmergencyContact,
    TMutationError,
    { id: string; data: Partial<TEmergencyContact> }
  >
): UseMutationResult<
  TEmergencyContact,
  TMutationError,
  { id: string; data: Partial<TEmergencyContact> }
> => {
  const { updateEmergencyContact } = useHealthAndSafetyService();
  const queryClient = useQueryClient();

  return useMutation<
    TEmergencyContact,
    TMutationError,
    { id: string; data: Partial<TEmergencyContact> }
  >({
    mutationFn: ({ id, data }) => updateEmergencyContact(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: [getEmergencyContactListKey] });
      queryClient.invalidateQueries({
        queryKey: [getEmergencyContactByIdKey, variables.id],
      });
    },
    ...options,
  });
};

// ===================== DELETE HOOK =====================
export const useDeleteEmergencyContactHook = (
  options?: UseMutationOptions<void, TMutationError, string>
): UseMutationResult<void, TMutationError, string> => {
  const { deleteEmergencyContact } = useHealthAndSafetyService();
  const queryClient = useQueryClient();

  return useMutation<void, TMutationError, string>({
    mutationFn: (id) => deleteEmergencyContact(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [getEmergencyContactListKey] });
    },
    ...options,
  });
};
